// Job-related types for the frontend

export type JobType = 'full_time' | 'part_time' | 'contract' | 'freelance' | 'internship';
export type ExperienceLevel = 'entry' | 'mid' | 'senior' | 'lead' | 'executive';
export type LocationType = 'remote' | 'onsite' | 'hybrid';
export type JobStatus = 'draft' | 'active' | 'paused' | 'closed' | 'filled';
export type ApplicationStatus =
  | 'pending'
  | 'viewed'
  | 'reviewing'
  | 'shortlisted'
  | 'interviewing'
  | 'offered'
  | 'accepted'
  | 'rejected'
  | 'withdrawn';

export interface JobSkill {
  id: string;
  job_id: string;
  skill_id: string;
  skill_name?: string;
  is_required: boolean;
  min_level?: string;
  years_experience?: number;
}

export interface Job {
  id: string;
  company_id: string;
  title: string;
  slug: string;
  description: string;
  job_type: JobType;
  experience_level: ExperienceLevel;
  category: string;
  location_type: LocationType;
  location_city?: string;
  location_country: string;
  timezone?: string;
  salary_min?: number;
  salary_max?: number;
  salary_currency: string;
  salary_period: string;
  show_salary: boolean;
  contract_duration_months?: number;
  contract_rate_min?: number;
  contract_rate_max?: number;
  requirements?: string;
  responsibilities?: string;
  benefits?: string;
  status: JobStatus;
  is_featured: boolean;
  is_urgent: boolean;
  application_email?: string;
  application_url?: string;
  application_deadline?: string;
  max_applications?: number;
  require_skill_verification: boolean;
  min_skill_level?: string;
  views_count: number;
  applications_count: number;
  published_at?: string;
  expires_at?: string;
  created_at: string;
  updated_at: string;
  skills?: JobSkill[];
  company_name?: string;
  company_logo?: string;
}

export interface JobSummary {
  id: string;
  title: string;
  slug: string;
  company_id: string;
  company_name: string;
  company_logo?: string;
  job_type: JobType;
  experience_level: ExperienceLevel;
  category: string;
  location_type: LocationType;
  location_city?: string;
  salary_min?: number;
  salary_max?: number;
  salary_currency: string;
  show_salary: boolean;
  is_featured: boolean;
  is_urgent: boolean;
  skills?: string[];
  published_at?: string;
}

export interface ExpertSummary {
  id: string;
  first_name: string;
  last_name: string;
  title?: string;
  avatar_url?: string;
  location?: string;
}

export interface JobApplication {
  id: string;
  job_id: string;
  expert_id: string;
  cover_letter?: string;
  resume_url?: string;
  portfolio_url?: string;
  proposed_rate?: number;
  proposed_start_date?: string;
  availability?: string;
  status: ApplicationStatus;
  rating?: number;
  viewed_at?: string;
  shortlisted_at?: string;
  interviewed_at?: string;
  offered_at?: string;
  decided_at?: string;
  created_at: string;
  updated_at: string;
  job?: JobSummary;
  expert?: ExpertSummary;
  unread_count?: number;
}

export interface ApplicationMessage {
  id: string;
  application_id: string;
  sender_type: 'company' | 'expert';
  sender_id: string;
  content: string;
  attachments?: string[];
  is_read: boolean;
  read_at?: string;
  created_at: string;
}

export interface SavedJob {
  id: string;
  job_id: string;
  expert_id: string;
  notes?: string;
  created_at: string;
  job?: JobSummary;
}

// Request types
export interface JobSearchParams {
  q?: string;
  category?: string;
  job_type?: JobType[];
  experience_level?: ExperienceLevel[];
  location_type?: LocationType[];
  city?: string;
  salary_min?: number;
  salary_max?: number;
  skill?: string[];
  company?: string;
  featured?: boolean;
  page?: number;
  limit?: number;
  sort?: 'relevance' | 'date' | 'salary';
  order?: 'asc' | 'desc';
}

export interface JobSearchResult {
  jobs: JobSummary[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface ApplyToJobRequest {
  cover_letter?: string;
  resume_url?: string;
  portfolio_url?: string;
  proposed_rate?: number;
  proposed_start_date?: string;
  availability?: string;
}

export interface UpdateApplicationStatusRequest {
  status: ApplicationStatus;
  company_notes?: string;
  rating?: number;
}

export interface SendMessageRequest {
  content: string;
  attachments?: string[];
}

// Display helpers
export const JOB_TYPE_LABELS: Record<JobType, string> = {
  full_time: 'Full Time',
  part_time: 'Part Time',
  contract: 'Contract',
  freelance: 'Freelance',
  internship: 'Internship',
};

export const EXPERIENCE_LEVEL_LABELS: Record<ExperienceLevel, string> = {
  entry: 'Entry Level',
  mid: 'Mid Level',
  senior: 'Senior',
  lead: 'Lead',
  executive: 'Executive',
};

export const LOCATION_TYPE_LABELS: Record<LocationType, string> = {
  remote: 'Remote',
  onsite: 'On-site',
  hybrid: 'Hybrid',
};

export const APPLICATION_STATUS_LABELS: Record<ApplicationStatus, string> = {
  pending: 'Pending Review',
  viewed: 'Viewed',
  reviewing: 'Under Review',
  shortlisted: 'Shortlisted',
  interviewing: 'Interviewing',
  offered: 'Offer Made',
  accepted: 'Accepted',
  rejected: 'Not Selected',
  withdrawn: 'Withdrawn',
};

export const JOB_CATEGORIES = [
  'Development',
  'DevOps',
  'Security',
  'Data',
  'AI/ML',
  'Cloud',
  'Management',
  'Support',
] as const;
